/**
 * Global live-audio recorder engine (singleton).
 * Recording continues while the modal is closed; state + chunks persist in IndexedDB
 * so a page refresh can auto-resume without pressing Record again.
 */

import {
  appendChunk,
  buildBlobFromChunks,
  clearActiveSession,
  clearChunks,
  deleteCompletedTake,
  getActiveSession,
  listCompletedTakes,
  peekActiveSessionPointer,
  putActiveSession,
  saveCompletedTake,
} from './liveAudioIdb';

const TIMESLICE_MS = 1000;
const LEVEL_TICK_MS = 80;

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

function extForMime(mime) {
  if (!mime) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a';
  return 'webm';
}

function newTakeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `take-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function computeElapsedMs(session, now = Date.now()) {
  if (!session?.startedAt) return 0;
  let paused = session.pausedAccumMs || 0;
  if (session.status === 'paused' && session.pauseStartedAt) {
    paused += Math.max(0, now - session.pauseStartedAt);
  }
  return Math.max(0, now - session.startedAt - paused);
}

class LiveAudioRecorderEngine {
  constructor() {
    this.listeners = new Set();
    this.mediaStream = null;
    /** Stereo MediaStream fed to MediaRecorder (mono mic duplicated L/R). */
    this.recordStream = null;
    this._graphMicStream = null;
    this.mediaRecorder = null;
    this.audioContext = null;
    this.analyser = null;
    this.levelTimer = null;
    this.elapsedTimer = null;
    this.beforeUnloadBound = null;
    this.bootstrappedKey = null;
    this.state = this.emptyState();
  }

  emptyState() {
    return {
      meetingId: null,
      userId: null,
      status: 'idle', // idle | recording | paused | stopped
      takeId: null,
      mimeType: '',
      startedAt: null,
      pausedAccumMs: 0,
      pauseStartedAt: null,
      elapsedMs: 0,
      level: 0,
      error: null,
      localTakes: [],
      ready: false,
      deviceId: null,
      devices: [],
      maxDurationMs: null,
    };
  }

  subscribe(fn) {
    this.listeners.add(fn);
    fn(this.getSnapshot());
    return () => this.listeners.delete(fn);
  }

  getSnapshot() {
    return { ...this.state, localTakes: [...this.state.localTakes] };
  }

  emit() {
    const snap = this.getSnapshot();
    this.listeners.forEach((fn) => {
      try {
        fn(snap);
      } catch {
        /* ignore subscriber errors */
      }
    });
  }

  patch(partial) {
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  async bindMeeting({ userId, meetingId }) {
    if (!userId || !meetingId) return;
    const key = `${userId}::${meetingId}`;
    if (this.bootstrappedKey === key && this.state.meetingId === meetingId) {
      await this.refreshLocalTakes();
      return;
    }

    // Switching meetings: park the previous active session in IDB and release the mic.
    if (this.state.meetingId && this.state.meetingId !== meetingId) {
      if (this.state.status === 'recording' || this.state.status === 'paused') {
        try {
          this.mediaRecorder?.requestData?.();
        } catch {
          /* ignore */
        }
        await this.persistSession();
        await this.detachRuntime(true);
        this.releaseMicHardware();
      }
    }

    const previousDeviceId = this.state.deviceId;
    this.bootstrappedKey = key;
    this.patch({
      ...this.emptyState(),
      meetingId,
      userId,
      deviceId: previousDeviceId,
      ready: false,
    });

    this.ensureDeviceChangeListener();

    try {
      const session = await getActiveSession(userId, meetingId);
      const localTakes = await listCompletedTakes(userId, meetingId);
      await this.refreshDevices();

      if (session && (session.status === 'recording' || session.status === 'paused')) {
        this.patch({
          meetingId,
          userId,
          status: session.status,
          takeId: session.takeId,
          mimeType: session.mimeType || pickMimeType(),
          startedAt: session.startedAt,
          pausedAccumMs: session.pausedAccumMs || 0,
          pauseStartedAt: session.pauseStartedAt || null,
          elapsedMs: computeElapsedMs(session),
          localTakes,
          deviceId: previousDeviceId,
          ready: true,
          error: null,
          maxDurationMs: session.maxDurationMs > 0 ? session.maxDurationMs : null,
        });
        await this.resumeRuntimeFromSession(session.status === 'paused');
      } else {
        this.patch({
          meetingId,
          userId,
          status: 'idle',
          takeId: null,
          mimeType: pickMimeType(),
          startedAt: null,
          pausedAccumMs: 0,
          pauseStartedAt: null,
          elapsedMs: 0,
          localTakes,
          deviceId: previousDeviceId,
          ready: true,
          error: null,
        });
      }
    } catch (e) {
      this.patch({
        ready: true,
        error: e?.message || 'بازیابی وضعیت ضبط ناموفق بود.',
        localTakes: [],
      });
    }

    this.ensureBeforeUnload();
  }

  async refreshLocalTakes() {
    const { userId, meetingId } = this.state;
    if (!userId || !meetingId) return;
    try {
      const localTakes = await listCompletedTakes(userId, meetingId);
      this.patch({ localTakes });
    } catch {
      /* ignore */
    }
  }

  ensureBeforeUnload() {
    if (typeof window === 'undefined' || this.beforeUnloadBound) return;
    this.beforeUnloadBound = (e) => {
      if (this.state.status === 'recording' || this.state.status === 'paused') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadBound);
  }

  async persistSession(extra = {}) {
    const { userId, meetingId, takeId, status, mimeType, startedAt, pausedAccumMs, pauseStartedAt } =
      { ...this.state, ...extra };
    if (!userId || !meetingId || !takeId) return;
    if (status !== 'recording' && status !== 'paused') {
      await clearActiveSession(userId, meetingId);
      return;
    }
    await putActiveSession({
      userId,
      meetingId,
      takeId,
      status,
      mimeType,
      startedAt,
      pausedAccumMs: pausedAccumMs || 0,
      pauseStartedAt: pauseStartedAt || null,
      maxDurationMs: this.state.maxDurationMs > 0 ? this.state.maxDurationMs : null,
      updatedAt: Date.now(),
    });
  }

  async refreshDevices() {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      this.patch({ devices: [] });
      return [];
    }
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const devices = all
        .filter((d) => d.kind === 'audioinput')
        .map((d, index) => ({
          deviceId: d.deviceId,
          groupId: d.groupId || '',
          label: d.label?.trim() || `میکروفن ${index + 1}`,
        }));
      this.patch({ devices });
      return devices;
    } catch {
      this.patch({ devices: [] });
      return [];
    }
  }

  ensureDeviceChangeListener() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    if (this._onDeviceChange) return;
    this._onDeviceChange = () => {
      this.refreshDevices().catch(() => {});
    };
    navigator.mediaDevices.addEventListener('devicechange', this._onDeviceChange);
  }

  async setDeviceId(deviceId) {
    if (this.state.status === 'recording' || this.state.status === 'paused') {
      throw new Error('برای تغییر میکروفن ابتدا ضبط را متوقف کنید.');
    }
    const nextId = deviceId || null;
    if (this.state.deviceId === nextId) return;
    this.patch({ deviceId: nextId });
    // استریم قبلی با دستگاه دیگر را آزاد کن تا start بعدی دستگاه جدید را بگیرد
    this.releaseMicHardware();
  }

  /**
   * Build (or reuse) a stereo MediaStream for MediaRecorder.
   * Mic input is usually mono; we duplicate it to L+R so the WebM/Opus
   * container is always 2-channel without relying on getUserMedia channelCount.
   */
  ensureStereoGraph(micStream) {
    if (
      this.recordStream &&
      this._graphMicStream === micStream &&
      this.audioContext &&
      this.audioContext.state !== 'closed'
    ) {
      const track = this.recordStream.getAudioTracks()?.[0];
      if (track && track.readyState === 'live') {
        return this.recordStream;
      }
    }

    this.teardownAudioGraph({ closeContext: true });

    const Ctx = typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : null;
    if (!Ctx || !micStream) {
      this.recordStream = micStream;
      this._graphMicStream = micStream;
      return this.recordStream;
    }

    try {
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.audioContext = new Ctx();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }

      const source = this.audioContext.createMediaStreamSource(micStream);

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);

      // Fan-out via gain nodes (safer than double-connect on some browsers)
      const left = this.audioContext.createGain();
      const right = this.audioContext.createGain();
      left.gain.value = 1;
      right.gain.value = 1;
      source.connect(left);
      source.connect(right);

      const merger = this.audioContext.createChannelMerger(2);
      left.connect(merger, 0, 0);
      right.connect(merger, 0, 1);

      const dest = this.audioContext.createMediaStreamDestination();
      try {
        dest.channelCount = 2;
        dest.channelCountMode = 'explicit';
        dest.channelInterpretation = 'speakers';
      } catch {
        /* some browsers reject explicit channelCount on destination */
      }
      merger.connect(dest);

      this.recordStream = dest.stream;
      this._graphMicStream = micStream;
      this.startLevelMeter();
      return this.recordStream;
    } catch {
      // Fall back to raw mic stream if Web Audio graph cannot be built
      this.analyser = null;
      this.recordStream = micStream;
      this._graphMicStream = micStream;
      return this.recordStream;
    }
  }

  teardownAudioGraph({ closeContext = true } = {}) {
    this.stopLevelMeter();
    this.analyser = null;
    this.recordStream = null;
    this._graphMicStream = null;
    if (closeContext && this.audioContext) {
      try {
        this.audioContext.close();
      } catch {
        /* ignore */
      }
      this.audioContext = null;
    }
  }

  releaseMicHardware() {
    this.teardownAudioGraph({ closeContext: true });
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
  }

  async ensureMic() {
    const wantedId = this.state.deviceId || undefined;
    if (this.mediaStream) {
      const currentId = this.mediaStream.getAudioTracks()?.[0]?.getSettings?.()?.deviceId;
      if (!wantedId || !currentId || currentId === wantedId) {
        this.ensureStereoGraph(this.mediaStream);
        return this.mediaStream;
      }
      this.releaseMicHardware();
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      throw new Error('مرورگر از ضبط صدا پشتیبانی نمی‌کند.');
    }
    const audio = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      // Prefer stereo when the device/browser can provide it; graph still guarantees 2ch output.
      channelCount: { ideal: 2 },
    };
    if (wantedId) {
      audio.deviceId = { exact: wantedId };
    }
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: false,
      });
    } catch (e) {
      // اگر deviceId نامعتبر بود، با پیش‌فرض سیستم تلاش کن
      if (wantedId && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
        this.patch({ deviceId: null });
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: { ideal: 2 },
          },
          video: false,
        });
      } else {
        throw e;
      }
    }
    this.ensureStereoGraph(this.mediaStream);
    await this.refreshDevices();
    // اگر هنوز deviceId انتخاب نشده، دستگاه واقعی استریم را به‌عنوان انتخاب ثبت کن
    if (!this.state.deviceId) {
      const liveId = this.mediaStream.getAudioTracks()?.[0]?.getSettings?.()?.deviceId;
      if (liveId) this.patch({ deviceId: liveId });
    }
    return this.mediaStream;
  }

  /** Stream that MediaRecorder must use (stereo when graph is available). */
  getRecordStream() {
    return this.recordStream || this.mediaStream;
  }

  startLevelMeter() {
    this.stopLevelMeter();
    const data = new Uint8Array(this.analyser?.frequencyBinCount || 0);
    this.levelTimer = window.setInterval(() => {
      if (!this.analyser || (this.state.status !== 'recording' && this.state.status !== 'paused')) {
        if (this.state.level !== 0) this.patch({ level: 0 });
        return;
      }
      this.analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / Math.max(data.length, 1));
      this.patch({ level: Math.min(1, rms * 3.2) });
    }, LEVEL_TICK_MS);
  }

  stopLevelMeter() {
    if (this.levelTimer) {
      clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
  }

  startElapsedTicker() {
    this.stopElapsedTicker();
    this.elapsedTimer = window.setInterval(() => {
      if (this.state.status !== 'recording' && this.state.status !== 'paused') return;
      const elapsedMs = computeElapsedMs(this.state);
      this.patch({ elapsedMs });
      const max = this.state.maxDurationMs;
      if (
        this.state.status === 'recording'
        && Number(max) > 0
        && elapsedMs >= max
        && !this.stoppingDueToMax
      ) {
        this.stoppingDueToMax = true;
        this.stop().finally(() => {
          this.stoppingDueToMax = false;
          this.patch({ error: 'سقف زمان صوت توضیحات تمام شد و ضبط متوقف شد.' });
        });
      }
    }, 250);
  }

  stopElapsedTicker() {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  async createRecorder(stream, mimeType) {
    const options = mimeType ? { mimeType } : undefined;
    let recorder;
    try {
      recorder = new MediaRecorder(stream, options);
    } catch {
      recorder = new MediaRecorder(stream);
    }

    recorder.ondataavailable = async (ev) => {
      if (!ev.data || ev.data.size === 0) return;
      const { userId, meetingId, takeId } = this.state;
      if (!userId || !meetingId || !takeId) return;
      try {
        await appendChunk(userId, meetingId, takeId, ev.data);
      } catch {
        /* best-effort persistence */
      }
    };

    recorder.onerror = () => {
      this.patch({ error: 'خطا در ضبط صدا. لطفاً دوباره تلاش کنید.' });
    };

    this.mediaRecorder = recorder;
    return recorder;
  }

  async resumeRuntimeFromSession(startPaused) {
    try {
      await this.ensureMic();
      const stream = this.getRecordStream();
      if (!stream) throw new Error('استریم ضبط در دسترس نیست.');
      const mimeType = this.state.mimeType || pickMimeType();
      const recorder = await this.createRecorder(stream, mimeType);
      recorder.start(TIMESLICE_MS);
      if (startPaused && typeof recorder.pause === 'function') {
        recorder.pause();
      }
      this.patch({ mimeType: recorder.mimeType || mimeType, error: null });
      this.startElapsedTicker();
      this.startLevelMeter();
    } catch (e) {
      this.patch({
        error:
          e?.name === 'NotAllowedError'
            ? 'دسترسی به میکروفون رد شد.'
            : e?.message || 'شروع مجدد ضبط ناموفق بود.',
      });
    }
  }

  async start(options = {}) {
    const { userId, meetingId } = this.state;
    if (!userId || !meetingId) throw new Error('جلسه مشخص نیست.');
    if (this.state.status === 'recording') return;

    if (this.state.status === 'paused') {
      await this.resume();
      return;
    }

    const maxDurationMs = Number(options.maxDurationMs) > 0 ? Number(options.maxDurationMs) : null;
    const mimeType = pickMimeType();
    const takeId = newTakeId();

    // ابتدا دسترسی میکروفن؛ شمارنده فقط بعد از موفقیت واقعی شروع می‌شود
    await this.ensureMic();
    const stream = this.getRecordStream();
    if (!stream) throw new Error('استریم ضبط در دسترس نیست.');
    const recorder = await this.createRecorder(stream, mimeType);
    const startedAt = Date.now();
    this.patch({
      status: 'recording',
      takeId,
      mimeType: recorder.mimeType || mimeType,
      startedAt,
      pausedAccumMs: 0,
      pauseStartedAt: null,
      elapsedMs: 0,
      error: null,
      maxDurationMs,
    });
    await this.persistSession();
    recorder.start(TIMESLICE_MS);
    this.startElapsedTicker();
    this.startLevelMeter();
  }

  async pause() {
    if (this.state.status !== 'recording' || !this.mediaRecorder) return;
    if (typeof this.mediaRecorder.pause !== 'function') {
      this.patch({ error: 'توقف موقت در این مرورگر پشتیبانی نمی‌شود.' });
      return;
    }
    try {
      this.mediaRecorder.requestData?.();
    } catch {
      /* ignore */
    }
    this.mediaRecorder.pause();
    const pauseStartedAt = Date.now();
    this.patch({ status: 'paused', pauseStartedAt, elapsedMs: computeElapsedMs({ ...this.state, status: 'paused', pauseStartedAt }) });
    await this.persistSession();
  }

  async resume() {
    if (this.state.status !== 'paused') return;

    let { pausedAccumMs, pauseStartedAt } = this.state;
    if (pauseStartedAt) {
      pausedAccumMs = (pausedAccumMs || 0) + Math.max(0, Date.now() - pauseStartedAt);
    }

    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      this.patch({
        status: 'recording',
        pausedAccumMs,
        pauseStartedAt: null,
      });
      await this.persistSession();
      await this.resumeRuntimeFromSession(false);
      return;
    }

    this.mediaRecorder.resume();
    this.patch({
      status: 'recording',
      pausedAccumMs,
      pauseStartedAt: null,
      elapsedMs: computeElapsedMs({
        ...this.state,
        status: 'recording',
        pausedAccumMs,
        pauseStartedAt: null,
      }),
    });
    await this.persistSession();
  }

  /**
   * After full page load, resume any parked active session for this user.
   */
  async bootstrapFromStorage(userId) {
    if (!userId) return;
    const pointer = peekActiveSessionPointer();
    if (!pointer || pointer.userId !== userId) return;
    if (this.bootstrappedKey === `${userId}::${pointer.meetingId}` && this.mediaRecorder) {
      return;
    }
    await this.bindMeeting({ userId, meetingId: pointer.meetingId });
  }

  async stop() {
    if (this.state.status !== 'recording' && this.state.status !== 'paused') return null;

    const { userId, meetingId, takeId, mimeType, startedAt } = this.state;
    const durationMs = computeElapsedMs(this.state);

    await new Promise((resolve) => {
      const recorder = this.mediaRecorder;
      if (!recorder || recorder.state === 'inactive') {
        resolve();
        return;
      }
      const finish = () => {
        recorder.removeEventListener('stop', finish);
        resolve();
      };
      recorder.addEventListener('stop', finish);
      try {
        recorder.requestData?.();
      } catch {
        /* ignore */
      }
      try {
        recorder.stop();
      } catch {
        resolve();
      }
      // safety
      setTimeout(resolve, 1500);
    });

    this.mediaRecorder = null;
    this.stopElapsedTicker();
    this.patch({ level: 0 });

    // Small delay so last ondataavailable can flush to IDB
    await new Promise((r) => setTimeout(r, 120));

    const blob = await buildBlobFromChunks(userId, meetingId, takeId, mimeType);
    if (!blob || blob.size === 0) {
      await clearChunks(userId, meetingId, takeId);
      await clearActiveSession(userId, meetingId);
      this.patch({
        status: 'idle',
        takeId: null,
        startedAt: null,
        pausedAccumMs: 0,
        pauseStartedAt: null,
        elapsedMs: 0,
        error: 'فایل ضبط خالی است. دوباره تلاش کنید.',
        maxDurationMs: null,
      });
      return null;
    }

    const take = {
      takeId,
      userId,
      meetingId,
      mimeType: mimeType || blob.type || 'audio/webm',
      durationMs,
      startedAt,
      createdAt: Date.now(),
      name: `ضبط ${new Date().toLocaleString('fa-IR')}`,
      size: blob.size,
      blob,
      extension: extForMime(mimeType || blob.type),
    };
    await saveCompletedTake(take);
    await clearChunks(userId, meetingId, takeId);
    await clearActiveSession(userId, meetingId);

    const localTakes = await listCompletedTakes(userId, meetingId);
    // Re-attach blobs for takes that only have metadata — current take has blob in memory
    const withBlob = localTakes.map((t) =>
      t.takeId === takeId ? { ...t, blob } : t,
    );

    this.patch({
      status: 'idle',
      takeId: null,
      startedAt: null,
      pausedAccumMs: 0,
      pauseStartedAt: null,
      elapsedMs: 0,
      localTakes: withBlob,
      error: null,
      maxDurationMs: null,
    });

    this.releaseMicIfIdle();
    return take;
  }

  /**
   * Discard current in-progress take without saving.
   */
  async discardCurrent() {
    const { userId, meetingId, takeId } = this.state;
    await this.detachRuntime(true);
    if (userId && meetingId && takeId) {
      await clearChunks(userId, meetingId, takeId);
    }
    if (userId && meetingId) {
      await clearActiveSession(userId, meetingId);
    }
    this.patch({
      status: 'idle',
      takeId: null,
      startedAt: null,
      pausedAccumMs: 0,
      pauseStartedAt: null,
      elapsedMs: 0,
      level: 0,
      error: null,
    });
    this.releaseMicIfIdle();
  }

  async removeLocalTake(takeId) {
    const { userId, meetingId } = this.state;
    if (!userId || !meetingId || !takeId) return;
    await deleteCompletedTake(userId, meetingId, takeId);
    await this.refreshLocalTakes();
  }

  async getTakeBlob(take) {
    if (take?.blob instanceof Blob) return take.blob;
    const { userId, meetingId } = this.state;
    if (take?.blob) return take.blob;
    // Takes saved with blob in IDB should deserialize as Blob
    if (take?.takeId) {
      const rows = await listCompletedTakes(userId, meetingId);
      const found = rows.find((t) => t.takeId === take.takeId);
      if (found?.blob instanceof Blob) return found.blob;
    }
    return null;
  }

  async detachRuntime(stopRecorder) {
    this.stopElapsedTicker();
    this.stopLevelMeter();
    if (stopRecorder && this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.ondataavailable = null;
        this.mediaRecorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.mediaRecorder = null;
  }

  releaseMicIfIdle() {
    if (this.state.status === 'recording' || this.state.status === 'paused') return;
    this.releaseMicHardware();
  }
}

export const liveAudioRecorder = new LiveAudioRecorderEngine();

export function formatRecorderTimer(ms) {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}
